import React from "react";
import {
  BarChart3,
  Eye,
  Gem,
  Handshake,
  Lightbulb,
  MapPin,
  ShieldCheck,
  Target,
} from "lucide-react";

const promises = [
  { Icon: Lightbulb, title: "Clarity", text: "I explain the process clearly, so you always know what comes next." },
  { Icon: ShieldCheck, title: "Confidence", text: "I help you compare the right options and choose what truly fits." },
  { Icon: Handshake, title: "Comfort", text: "I keep the experience calm, personal, and easy to navigate." },
];

const cards = [
  {
    Icon: Target,
    title: <>My <span>Mission</span></>,
    body: "To help people find the right vehicle for their lifestyle and budget through honest guidance, personal support, and a smoother buying journey from first enquiry to final handover.",
    footer: "Real support. Real people.",
  },
  {
    Icon: Eye,
    title: <>My <span>Vision</span></>,
    body: "To be a trusted and recognised name in pre-owned vehicle sales, known for exceptional service, long-term customer relationships and making a positive impact in every journey I’m part of.",
    footer: "More drivers. Brighter journeys.",
  },
  {
    Icon: Gem,
    title: <>My <span>Values</span></>,
    points: ["People first, always", "Honesty and transparency", "Respect and genuine care", "Consistency and accountability"],
    footer: "Trust. Integrity. People.",
  },
  {
    Icon: BarChart3,
    title: <>My <span>Goals</span></>,
    points: ["Help more people find reliable vehicles", "Build long-term customer relationships", "Deliver an even smoother buying experience", "Keep improving my product knowledge"],
    footer: "Bigger opportunities ahead.",
  },
];

export const PurposeSection: React.FC = () => (
  <section id="purpose" className="purpose-section" aria-labelledby="purpose-title">
    <div className="container-x">
      <div className="purpose-panel">
        <div className="purpose-top">
          <div className="purpose-copy">
            <p className="purpose-label">My Purpose <span /></p>
            <h2 id="purpose-title">People First.<br /><span>Journeys That Matter.</span></h2>
            <p>At the heart of everything I do is people. I believe a car is more than just a vehicle — it’s a step towards someone’s goals, independence, family, and future. My purpose is to make the car buying experience simple, transparent, and enjoyable, while building lasting relationships based on trust and genuine care.</p>
          </div>

          <div className="purpose-visual">
            <div className="purpose-photo">
              <img src="/images/dealership-reference.png" alt="Toyota dealership exterior" loading="lazy" />
            </div>
          </div>
        </div>

        <div className="purpose-promise">
          <div className="purpose-promise-intro"><p>My Promise</p><em>A better buying experience,<br />for a brighter tomorrow.</em></div>
          {promises.map(({ Icon, title, text }) => <div className="purpose-promise-item" key={title}><Icon aria-hidden="true" /><div><strong>{title}</strong><p>{text}</p></div></div>)}
        </div>

        <div className="purpose-cards">
          {cards.map(({ Icon, title, body, points, footer }, index) => (
            <article key={index}>
              <Icon aria-hidden="true" />
              <h3>{title}</h3>
              {body && <p>{body}</p>}
              {points && <ul>{points.map((point) => <li key={point}>{point}</li>)}</ul>}
              <small>{footer}</small>
            </article>
          ))}
        </div>
        <div className="purpose-closing" style={{ backgroundImage: 'url("/images/purpose-road.png")' }}>
          <div><p><span />The Journey Continues</p><h3>Good People. <strong>Great Journeys.</strong></h3><small>Driven by people. Focused on what’s next.</small></div>
          <blockquote><MapPin aria-hidden="true" /><span>“It’s not just about cars,<br />it’s about people.”<cite>— Simphiwe Fudwana</cite></span></blockquote>
        </div>
      </div>
    </div>
  </section>
);

export default PurposeSection;
